import { Spin, Empty, Typography, Table, Tabs, Button, Modal, Form, Input, Select, Row, Col, Tag, Switch } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import { formatDate } from '../../helpers/date';

interface RouterTableDataType {
  key: number;
  intPkSiteCoreRouterID: number;
  routerName: string;
  coreRouterIp: string;
  coreRouterVendorName: string;
  onlineUsersCount?: number;
   enable?: number;
}

interface SiteDetailsType {
  intPkCompanySiteID: number;
  intPkCompanyID: number;
  siteName: string;
  siteAddress: string;
  enable: number;
  dateCreated: string;
}

const MasterStaffSiteDetailsComponent = () => {
  const { intPkCompanySiteID } = useParams<{ intPkCompanySiteID: string }>();
  const navigate = useNavigate();
  const { openNotification, contextHolder } = useMultiNotification();

  // Site details API
  const [
    postSiteDetailsApiData,
    siteDetailsApiResponse,
    siteDetailsApiError,
    siteDetailsApiLoading,
    siteDetailsApiMessage,
    siteDetailsApiErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/site-details');

  // Routers API
  const [
    postRoutersApiData,
    routersApiResponse,
    routersApiError,
    routersApiLoading,
    routersApiMessage,
    routersApiErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/site-routers');

  const [siteDetails, setSiteDetails] = useState<SiteDetailsType | null>(null);
  const [routers, setRouters] = useState<RouterTableDataType[]>([]);
  const [form] = Form.useForm();
  const [isCreateRouterModalOpen, setIsCreateRouterModalOpen] = useState(false);
  const [vendors, setVendors] = useState<any[]>([]);
  const postSiteDetailsApiDataRef = useRef(postSiteDetailsApiData);
  const postRoutersApiDataRef = useRef(postRoutersApiData);
  const lastProcessedErrorCount = useRef({
    site: 0,
    routers: 0,
    createRouter: 0,
  });
  const hasProcessedCreateRouterSuccess = useRef(false);
  const lastProcessedCreateRouterResponse = useRef<any>(null);

  // Create router API
  const [
    createRouterData,
    createRouterResponse,
    createRouterError,
    createRouterLoading,
    createRouterMessage,
    createRouterErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/create-router');

  // Vendors API
  const [fetchVendorsData, vendorsResponse, vendorsError, vendorsLoading, vendorsMessage, vendorsErrorCount] =
    useAxiosPost<ResponseProp>('companies/vendors');

  // Site update / toggle APIs
  const [siteForm] = Form.useForm();
  const [isEditSiteModalOpen, setIsEditSiteModalOpen] = useState(false);

  const [
    updateSiteData,
    updateSiteResponse,
    updateSiteError,
    updateSiteLoading,
    updateSiteMessage,
    updateSiteErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/update-site');

  const [
    toggleSiteStatusData,
    toggleSiteStatusResponse,
    toggleSiteStatusError,
    toggleSiteStatusLoading,
    toggleSiteStatusMessage,
    toggleSiteStatusErrorCount,
  ] = useAxiosPost<ResponseProp>('companies/toggle-site-status');

  const [isToggleSiteModalOpen, setIsToggleSiteModalOpen] = useState(false);
  const [pendingSiteEnableValue, setPendingSiteEnableValue] = useState<number | null>(null);
  const lastProcessedUpdateSiteResponse = useRef<any>(null);
  const lastProcessedToggleSiteResponse = useRef<any>(null);

  // Keep refs updated
  useEffect(() => {
    postSiteDetailsApiDataRef.current = postSiteDetailsApiData;
    postRoutersApiDataRef.current = postRoutersApiData;
  }, [postSiteDetailsApiData, postRoutersApiData]);

  // Fetch data when component mounts or site ID changes
  useEffect(() => {
    if (intPkCompanySiteID) {
      postSiteDetailsApiDataRef.current({
        intPkCompanySiteID: Number(intPkCompanySiteID),
      });
      postRoutersApiDataRef.current({
        intPkCompanySiteID: Number(intPkCompanySiteID),
      });
      fetchVendorsData({} as any);
    }
  }, [intPkCompanySiteID]);

  // Handle vendors response
  useEffect(() => {
    if (vendorsResponse !== null) {
      const responseData: any = vendorsResponse.responseData;
      if (Array.isArray(responseData)) {
        setVendors(responseData);
      }
    }
  }, [vendorsResponse]);

  // Handle site details response
  useEffect(() => {
    if (siteDetailsApiResponse !== null) {
      const responseData: any = siteDetailsApiResponse.responseData;
      setSiteDetails(responseData);
    }
  }, [siteDetailsApiResponse]);

  // Handle routers response
  useEffect(() => {
    if (routersApiResponse !== null) {
      const responseData: any = routersApiResponse.responseData;
      if (Array.isArray(responseData)) {
        const routersWithKeys = responseData.map((router: any) => ({
          ...router,
          key: router.intPkSiteCoreRouterID,
        }));
        setRouters(routersWithKeys);
      } else {
        setRouters([]);
      }
    }
  }, [routersApiResponse]);

  // Handle site details error
  useEffect(() => {
    if (
      siteDetailsApiError !== null &&
      siteDetailsApiErrorCount !== 0 &&
      lastProcessedErrorCount.current.site !== siteDetailsApiErrorCount
    ) {
      lastProcessedErrorCount.current.site = siteDetailsApiErrorCount;
      openNotification('error', 'Failed to Load Site Details', siteDetailsApiError);
    }
  }, [siteDetailsApiError, siteDetailsApiErrorCount, openNotification]);

  // Handle routers error
  useEffect(() => {
    if (
      routersApiError !== null &&
      routersApiErrorCount !== 0 &&
      lastProcessedErrorCount.current.routers !== routersApiErrorCount
    ) {
      lastProcessedErrorCount.current.routers = routersApiErrorCount;
      openNotification('error', 'Failed to Load Routers', routersApiError);
    }
  }, [routersApiError, routersApiErrorCount, openNotification]);

  const handleRouterRowClick = (record: RouterTableDataType) => {
    navigate(`/master-staff-router-details/${record.intPkSiteCoreRouterID}`);
  };

  const showCreateRouterModal = () => {
    setIsCreateRouterModalOpen(true);
  };

  const handleCreateRouterCancel = () => {
    if (!createRouterLoading) {
      setIsCreateRouterModalOpen(false);
      form.resetFields();
    }
  };

  const handleCreateRouter = (values: any) => {
    const routerData = {
      intPkCompanySiteID: Number(intPkCompanySiteID),
      routerName: values.routerName.trim(),
      eUserLocalNetworkLinkLogin: values.eUserLocalNetworkLinkLogin.trim(),
      coreRouterIp: values.coreRouterIp.trim(),
      password: values.password.trim(),
      username: values.username.trim(),
      intPkCoreRouterVendorsID: Number(values.intPkCoreRouterVendorsID),
      voucherAuthBotSecret: values.voucherAuthBotSecret.trim(),
      eUserInitProxyURL: values.eUserInitProxyURL.trim(),
      eUserVoucherCheckProxyURL: values.eUserVoucherCheckProxyURL.trim(),
      eUserOpenPageAfterSuccessLogin: values.eUserOpenPageAfterSuccessLogin?.trim() || null,
      routerCryptoKey: values.routerCryptoKey.trim(),
    };
    createRouterData(routerData as any);
  };

  const handleEditSiteClick = () => {
    if (!siteDetails) return;
    siteForm.setFieldsValue({
      siteName: siteDetails.siteName,
      siteAddress: siteDetails.siteAddress,
    });
    setIsEditSiteModalOpen(true);
  };

  const handleEditSiteCancel = () => {
    if (!updateSiteLoading) {
      setIsEditSiteModalOpen(false);
      siteForm.resetFields();
    }
  };

  const handleUpdateSite = (values: { siteName: string; siteAddress: string }) => {
    if (!siteDetails) return;
    const payload = {
      id: siteDetails.intPkCompanySiteID,
      siteName: values.siteName.trim(),
      siteAddress: values.siteAddress.trim(),
    };
    updateSiteData(payload as any);
  };

  const handleSiteStatusToggle = (checked: boolean) => {
    const newEnable = checked ? 1 : 0;
    setPendingSiteEnableValue(newEnable);
    setIsToggleSiteModalOpen(true);
  };

  const handleConfirmToggleSite = () => {
    if (!siteDetails || pendingSiteEnableValue === null) return;
    toggleSiteStatusData({
      id: siteDetails.intPkCompanySiteID,
      enable: pendingSiteEnableValue,
    } as any);
  };

  const handleCancelToggleSite = () => {
    setIsToggleSiteModalOpen(false);
    setPendingSiteEnableValue(null);
  };

  // Handle update site success
  useEffect(() => {
    if (updateSiteResponse !== null && updateSiteResponse !== lastProcessedUpdateSiteResponse.current) {
      lastProcessedUpdateSiteResponse.current = updateSiteResponse;
      const responseData: any = updateSiteResponse.responseData;
      if (responseData) {
        setSiteDetails(responseData);
      }
      const backendMessage = (updateSiteResponse as any).message || 'Company site updated successfully';
      openNotification('success', 'Site Updated', backendMessage);
      setIsEditSiteModalOpen(false);
      siteForm.resetFields();
    }
  }, [updateSiteResponse, openNotification]);

  // Handle update site error
  useEffect(() => {
    if (updateSiteError !== null && updateSiteErrorCount !== 0) {
      openNotification('error', 'Failed to Update Site', updateSiteError);
    }
  }, [updateSiteError, updateSiteErrorCount, openNotification]);

  // Handle toggle site status success
  useEffect(() => {
    if (toggleSiteStatusResponse !== null && toggleSiteStatusResponse !== lastProcessedToggleSiteResponse.current) {
      lastProcessedToggleSiteResponse.current = toggleSiteStatusResponse;
      const responseData: any = toggleSiteStatusResponse.responseData;
      if (responseData) {
        setSiteDetails(responseData);
      }
      const backendMessage =
        (toggleSiteStatusResponse as any).message ||
        `Company site ${
          (toggleSiteStatusResponse as any)?.responseData?.enable === 1 ? 'enabled' : 'disabled'
        } successfully`;
      openNotification('success', 'Site Status Updated', backendMessage);
      setIsToggleSiteModalOpen(false);
      setPendingSiteEnableValue(null);
    }
  }, [toggleSiteStatusResponse, openNotification]);

  // Handle toggle site status error
  useEffect(() => {
    if (toggleSiteStatusError !== null && toggleSiteStatusErrorCount !== 0) {
      openNotification('error', 'Failed to Update Site Status', toggleSiteStatusError);
      setIsToggleSiteModalOpen(false);
      setPendingSiteEnableValue(null);
    }
  }, [toggleSiteStatusError, toggleSiteStatusErrorCount, openNotification]);

  // Handle create router success
  useEffect(() => {
    if (createRouterResponse !== null && createRouterResponse !== lastProcessedCreateRouterResponse.current) {
      lastProcessedCreateRouterResponse.current = createRouterResponse;
      hasProcessedCreateRouterSuccess.current = true;
      // Reset error count to prevent showing error after success
      lastProcessedErrorCount.current.createRouter = createRouterErrorCount;
      openNotification('success', 'Router Created', 'Router created successfully');
      setIsCreateRouterModalOpen(false);
      form.resetFields();
      // Refresh routers list
      setTimeout(() => {
        if (intPkCompanySiteID) {
          postRoutersApiDataRef.current({
            intPkCompanySiteID: Number(intPkCompanySiteID),
          });
        }
      }, 100);
    }
  }, [createRouterResponse]);

  // Reset the flag when modal opens or createRouterResponse becomes null
  useEffect(() => {
    if (createRouterResponse === null) {
      hasProcessedCreateRouterSuccess.current = false;
      lastProcessedCreateRouterResponse.current = null;
    }
  }, [createRouterResponse]);

  // Handle create router error - only show if we haven't already processed a success
  useEffect(() => {
    if (
      createRouterError !== null &&
      createRouterErrorCount !== 0 &&
      lastProcessedErrorCount.current.createRouter !== createRouterErrorCount &&
      !hasProcessedCreateRouterSuccess.current
    ) {
      lastProcessedErrorCount.current.createRouter = createRouterErrorCount;
      openNotification('error', 'Failed to Create Router', createRouterError);
    }
  }, [createRouterError, createRouterErrorCount, openNotification]);

  const routerColumns: ColumnsType<RouterTableDataType> = [
    {
      title: 'Router Name',
      dataIndex: 'routerName',
      key: 'routerName',
      render: (text: string) => <Typography.Text strong>{text || '-'}</Typography.Text>,
    },
    {
      title: 'IP Address',
      dataIndex: 'coreRouterIp',
      key: 'coreRouterIp',
      render: (text: string) => text || '-',
    },
    {
      title: 'Vendor',
      dataIndex: 'coreRouterVendorName',
      key: 'coreRouterVendorName',
      render: (text: string) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'enable',
      key: 'enable',
      render: (value: number | undefined) => {
        const isEnabled = value === undefined ? true : Number(value) === 1;
        return <Tag color={isEnabled ? 'success' : 'default'}>{isEnabled ? 'Enabled' : 'Disabled'}</Tag>;
      },
    },
    {
      title: 'Online Users',
      dataIndex: 'onlineUsersCount',
      key: 'onlineUsersCount',
      render: (count: number) => count ?? 0,
      align: 'center',
    },
  ];

  if (siteDetailsApiLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <Typography style={{ marginTop: '16px', display: 'block' }}>Loading site details...</Typography>
      </div>
    );
  }

  if (siteDetailsApiError && !siteDetailsApiLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Empty
          description={
            <Typography.Text type="danger">{siteDetailsApiError || 'Failed to load site details'}</Typography.Text>
          }
        />
      </div>
    );
  }

  if (!siteDetails && !siteDetailsApiLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Empty description="Site details not found" />
      </div>
    );
  }

  const tabItems = [
    {
      key: 'routers',
      label: 'Routers',
      children: (
        <>
          <div
            style={{
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={showCreateRouterModal} size="large">
              Create Router
            </Button>
          </div>
          {routersApiLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <Typography style={{ marginTop: '16px', display: 'block' }}>Loading routers...</Typography>
            </div>
          ) : routersApiError && !routersApiLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Empty
                description={
                  <Typography.Text type="danger">{routersApiError || 'Failed to load routers'}</Typography.Text>
                }
              />
            </div>
          ) : routers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Empty description="No routers found for this site." />
            </div>
          ) : (
            <div className="table-container-custom">
              <Table
                className="antd-table-striped router-table-clickable sites-table-clickable"
                columns={routerColumns}
                dataSource={routers}
                pagination={false}
                loading={routersApiLoading}
                rowClassName={(record) =>
                  record.enable !== undefined && Number(record.enable) === 0 ? 'router-disabled' : ''
                }
                onRow={(record) => ({
                  onClick: () => handleRouterRowClick(record),
                  style: { cursor: 'pointer' },
                })}
              />
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      {/* Create Router Modal */}
      <Modal
        title="Create New Router"
        open={isCreateRouterModalOpen}
        onCancel={handleCreateRouterCancel}
        footer={null}
        width={700}
        maskClosable={!createRouterLoading}
        closable={!createRouterLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateRouter} requiredMark={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Router Name</span>}
                name="routerName"
                rules={[
                  { required: true, message: 'Please enter router name' },
                  {
                    max: 100,
                    message: 'Router name must be less than 100 characters',
                  },
                ]}
              >
                <Input placeholder="Enter router name" disabled={createRouterLoading} maxLength={100} showCount />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Vendor</span>}
                name="intPkCoreRouterVendorsID"
                rules={[{ required: true, message: 'Please select a vendor' }]}
              >
                <Select
                  placeholder="Select vendor"
                  disabled={createRouterLoading || vendorsLoading}
                  loading={vendorsLoading}
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={vendors.map((vendor: any) => ({
                    value: vendor.intPkCoreRouterVendorsID,
                    label: vendor.coreRouterVendorName,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Core Router IP</span>}
                name="coreRouterIp"
                rules={[
                  { required: true, message: 'Please enter core router IP' },
                  {
                    max: 45,
                    message: 'IP address must be less than 45 characters',
                  },
                ]}
              >
                <Input placeholder="Enter core router IP" disabled={createRouterLoading} maxLength={45} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Username</span>}
                name="username"
                rules={[
                  { required: true, message: 'Please enter username' },
                  {
                    max: 45,
                    message: 'Username must be less than 45 characters',
                  },
                ]}
              >
                <Input placeholder="Enter username" disabled={createRouterLoading} maxLength={45} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Password</span>}
            name="password"
            rules={[
              { required: true, message: 'Please enter password' },
              { max: 45, message: 'Password must be less than 45 characters' },
            ]}
          >
            <Input placeholder="Enter password" disabled={createRouterLoading} maxLength={45} />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>E-User Local Network Link Login</span>}
            name="eUserLocalNetworkLinkLogin"
            rules={[
              {
                required: true,
                message: 'Please enter local network link login',
              },
              { max: 300, message: 'Link must be less than 300 characters' },
            ]}
          >
            <Input
              placeholder="Enter local network link login"
              disabled={createRouterLoading}
              maxLength={300}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>E-User Init Proxy URL</span>}
            name="eUserInitProxyURL"
            rules={[
              { required: true, message: 'Please enter init proxy URL' },
              { max: 300, message: 'URL must be less than 300 characters' },
            ]}
          >
            <Input placeholder="Enter init proxy URL" disabled={createRouterLoading} maxLength={300} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>E-User Voucher Check Proxy URL</span>}
            name="eUserVoucherCheckProxyURL"
            rules={[
              {
                required: true,
                message: 'Please enter voucher check proxy URL',
              },
              { max: 300, message: 'URL must be less than 300 characters' },
            ]}
          >
            <Input
              placeholder="Enter voucher check proxy URL"
              disabled={createRouterLoading}
              maxLength={300}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>E-User Open Page After Success Login</span>}
            name="eUserOpenPageAfterSuccessLogin"
            rules={[{ max: 300, message: 'URL must be less than 300 characters' }]}
          >
            <Input
              placeholder="Enter open page URL (optional)"
              disabled={createRouterLoading}
              maxLength={300}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Voucher Auth Bot Secret</span>}
            name="voucherAuthBotSecret"
            rules={[
              {
                required: true,
                message: 'Please enter voucher auth bot secret',
              },
              {
                max: 100,
                message: 'Voucher auth bot secret must be less than 100 characters',
              },
            ]}
          >
            <Input
              placeholder="Enter voucher auth bot secret"
              disabled={createRouterLoading}
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Router Crypto Key</span>}
            name="routerCryptoKey"
            rules={[
              { required: true, message: 'Please enter router crypto key' },
              {
                max: 300,
                message: 'Crypto key must be less than 300 characters',
              },
            ]}
          >
            <Input placeholder="Enter router crypto key" disabled={createRouterLoading} maxLength={300} showCount />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleCreateRouterCancel} style={{ marginRight: '8px' }} disabled={createRouterLoading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={createRouterLoading} disabled={createRouterLoading}>
              {createRouterLoading ? 'Creating...' : 'Create Router'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      {/* Edit Site Modal */}
      <Modal
        title="Update Site"
        open={isEditSiteModalOpen}
        onCancel={handleEditSiteCancel}
        footer={null}
        width={600}
        maskClosable={!updateSiteLoading}
        closable={!updateSiteLoading}
      >
        <Form form={siteForm} layout="vertical" onFinish={handleUpdateSite} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Site Name</span>}
            name="siteName"
            rules={[
              { required: true, message: 'Please enter site name' },
              { max: 100, message: 'Site name must be less than 100 characters' },
            ]}
          >
            <Input placeholder="Enter site name" disabled={updateSiteLoading} maxLength={100} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Site Address</span>}
            name="siteAddress"
            rules={[
              { required: true, message: 'Please enter site address' },
              { max: 200, message: 'Site address must be less than 200 characters' },
            ]}
          >
            <Input.TextArea
              placeholder="Enter site address"
              rows={3}
              disabled={updateSiteLoading}
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleEditSiteCancel} style={{ marginRight: '8px' }} disabled={updateSiteLoading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={updateSiteLoading} disabled={updateSiteLoading}>
              {updateSiteLoading ? 'Updating...' : 'Update Site'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Toggle Site Status Confirmation Modal */}
      <Modal
        title="Confirm Status Change"
        open={isToggleSiteModalOpen}
        onOk={handleConfirmToggleSite}
        onCancel={handleCancelToggleSite}
        confirmLoading={toggleSiteStatusLoading}
        okText="Confirm"
        cancelText="Cancel"
        width={400}
      >
        <Typography.Paragraph>
          {pendingSiteEnableValue === 1
            ? 'Are you sure you want to enable this site?'
            : 'Are you sure you want to disable this site? All routers under this site may be affected.'}
        </Typography.Paragraph>
      </Modal>

      <div style={{ padding: '24px' }}>
        <Typography.Title level={2}>Site Details</Typography.Title>
        {siteDetails && (
          <div
            style={{
              marginBottom: '24px',
              padding: '16px',
              background: '#f5f5f5',
              borderRadius: '8px',
            }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Typography.Text strong>Site Name: </Typography.Text>
                <Typography.Text>{siteDetails.siteName}</Typography.Text>
                <br />
                <Typography.Text strong>Address: </Typography.Text>
                <Typography.Text>{siteDetails.siteAddress}</Typography.Text>
                <br />
                <Typography.Text strong>Date Created: </Typography.Text>
                <Typography.Text>
                  {siteDetails.dateCreated ? formatDate(siteDetails.dateCreated) : '-'}
                </Typography.Text>
              </Col>
              <Col>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button type="link" onClick={handleEditSiteClick}>
                    Edit Site
                  </Button>
                  <Switch
                    checked={Number(siteDetails.enable) === 1}
                    onChange={handleSiteStatusToggle}
                    loading={toggleSiteStatusLoading}
                    checkedChildren="Enabled"
                    unCheckedChildren="Disabled"
                  />
                  <Tag color={Number(siteDetails.enable) === 1 ? 'success' : 'default'}>
                    {Number(siteDetails.enable) === 1 ? 'Enabled' : 'Disabled'}
                  </Tag>
                </div>
              </Col>
            </Row>
          </div>
        )}
        <Tabs defaultActiveKey="routers" items={tabItems} />
      </div>
    </>
  );
};

export default MasterStaffSiteDetailsComponent;
